import React, { useEffect, useRef } from 'react';
import { Button, Uploader } from 'rsuite';
import { FileType, UploaderInstance } from 'rsuite/esm/Uploader/Uploader';

const MAX_FILESIZE_IN_BYTES = 4194304;
const MIN_FILESIZE_IN_BYTES = 15360;

const extensionDict = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.pdf': 'application/pdf',
  '.heic': 'image/heic',
};

const validExtensionLabels = Object.keys(extensionDict)
  .map((ext) => ext.replace('.', '').toUpperCase())
  .join(', ')
  .replace(/,([^,]*)$/, ', or$1');

const FILE_TYPE_ERROR = `Upload a ${validExtensionLabels} file.`;
const FILE_SIZE_ERROR = `Upload a file between ${Math.round(MIN_FILESIZE_IN_BYTES / 1024)} KB and ${Math.round(MAX_FILESIZE_IN_BYTES / (1024 * 1024))} MB.`;

interface DocumentUploadProps {
  allowMultiple?: boolean;
  ariaLabel?: string;
  fileList: Array<FileType>;
  maxDocuments?: number;
  onChange?: (fileList?: Array<FileType>) => void;
  onError?: (err: string) => void;
  validExtensions?: Array<string>;
}
const DocumentUpload = ({
  allowMultiple = true,
  ariaLabel = '',
  fileList,
  maxDocuments = 20,
  onChange = () => null,
  onError = () => null,
  validExtensions = ['*/*'],
}: DocumentUploadProps) => {
  const ref = useRef<UploaderInstance>(null);

  useEffect(() => {
    if (!ref?.current) return;
    const target = ref.current.root.querySelector('input');
    if (target) {
      target.setAttribute('aria-label', `${ariaLabel}-label`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  const onDocumentUploadChanged = (_value: Array<FileType>) => {
    let value;
    let doChange = true;
    if (_value && _value.length > 0) {
      value =
        _value.length > maxDocuments ? _value.slice(0, maxDocuments) : _value;
      value = value.map((file) => {
        if (file.name?.endsWith('.heic') && file.blobFile?.type === '') {
          // This is required due to some problems in the way some browser assign mime types for heic files. Heic is not a format that is supported widely in browsers
          // The solution is to add the type manually into the file.
          // Check https://deckardtech.atlassian.net/browse/ES-820
          return {
            ...file,
            blobFile: new File([file.blobFile as File], file.name, {
              type: 'image/heic',
            }),
          };
        }
        return file;
      });
      doChange = _value.every((v) => isValidFileType(v.blobFile));
    }
    if (doChange) {
      onError('');
      onChange(value);
    }
  };

  const isValidFileType = (blob?: File) => {
    if (!blob) return false;
    const splitFileName = blob?.name.split('.');
    const fileExtension = `.${splitFileName[splitFileName.length - 1].toLowerCase()}`;
    const hasValidExtension =
      Object.keys(extensionDict).includes(fileExtension) ||
      fileExtension === '.heic';
    if (!hasValidExtension) return false;
    if (fileExtension === '.heic') return true;
    return (
      blob.type === extensionDict[fileExtension as keyof typeof extensionDict]
    );
  };

  const isValidFileSize = (blob?: File): boolean => {
    if (!blob) return false;
    return (
      blob.size >= MIN_FILESIZE_IN_BYTES && blob.size <= MAX_FILESIZE_IN_BYTES
    );
  };

  const onShouldUpdateQueue = (queue: Array<FileType>) => {
    if (queue?.length > maxDocuments) {
      onError(
        `You have selected too many files. Upload no more than ${maxDocuments} documents.`
      );
      return false;
    }
    if (!queue.every((q) => isValidFileType(q.blobFile))) {
      onError(FILE_TYPE_ERROR);
      return false;
    }
    if (!queue.every((q) => isValidFileSize(q.blobFile))) {
      onError(FILE_SIZE_ERROR);
      return false;
    }
    return true;
  };

  return (
    <Uploader
      accept={`${validExtensions.join(', ')}`}
      action=""
      autoUpload={false}
      data-testid="document-upload"
      fileList={fileList}
      multiple={allowMultiple}
      onChange={onDocumentUploadChanged}
      ref={ref}
      shouldQueueUpdate={onShouldUpdateQueue}
    >
      <Button data-cy={ariaLabel}>Select files...</Button>
    </Uploader>
  );
};

export default DocumentUpload;
